import {
    DEFAULT_TIMING_CONFIG,
    useStreamingText,
} from '@rhg/hooks/use-streaming-text';
import { Typography } from '@rhg/libs/material';
import { StreamingToken } from './streaming-token';
import { StreamingTypographyProps } from './types';

export { StreamingToken } from './streaming-token';
export type {
    StreamingTypographyProps,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- re-exporting for backward compatibility
    StreamingTextProps,
    StreamingProps,
    StreamingTokenProps,
} from './types';

/**
 * StreamingTypography component for ChatGPT-like word-by-word text animation.
 *
 * Extends MUI Typography with streaming animation capabilities. Reveals text
 * progressively with fade-in effects, simulating how AI assistants stream responses.
 *
 * ## Features
 *
 * - **Word-by-word reveal**: Each word fades in individually
 * - **Variant-aware timing**: Larger text (h1-h4) animates slower for readability
 * - **Natural pauses**: Extra delay after punctuation and newlines
 * - **Accessible**: Full text available via aria-label for screen readers
 * - **Full Typography API**: Supports all MUI Typography props
 *
 * ## Usage
 *
 * @example Basic usage
 * ```tsx
 * <StreamingTypography>Hello, world!</StreamingTypography>
 * ```
 *
 * @example With completion callback
 * ```tsx
 * <StreamingTypography onComplete={() => setShowNextButton(true)}>
 *   Loading complete.
 * </StreamingTypography>
 * ```
 *
 * @example As a heading element
 * ```tsx
 * <StreamingTypography variant="h3" component="h1">
 *   Welcome to the app
 * </StreamingTypography>
 * ```
 *
 * @example Custom timing (slower, more deliberate)
 * ```tsx
 * <StreamingTypography timing={{ baseDelayMs: 120, punctuationDelayMs: 300 }}>
 *   Important message...
 * </StreamingTypography>
 * ```
 *
 * @example Capped reveal — type for 1s, then fade the rest in over 0.5s
 * ```tsx
 * <StreamingTypography
 *   timing={{ maxTypingDurationMs: 1000, bulkRevealDurationMs: 500 }}
 * >
 *   {longResponse}
 * </StreamingTypography>
 * ```
 */
export function StreamingTypography({
    // Streaming-specific props
    children,
    tokenSx,
    timing,
    onComplete,
    onStart,
    autoStart = true,
    disableAnimation = false,
    // Typography props with defaults
    variant = 'body1',
    // Rest of Typography props
    ...typographyProps
}: StreamingTypographyProps) {
    const { tokens, bulkRevealStartIndex } = useStreamingText({
        text: children,
        autoStart,
        timing,
        variant,
        onComplete,
        onStart,
        disableAnimation,
    });

    const mergedTiming = { ...DEFAULT_TIMING_CONFIG, ...timing };

    // Default aria-label to children if not provided
    const ariaLabel = typographyProps['aria-label'] ?? children;

    return (
        <Typography
            variant={variant}
            {...typographyProps}
            aria-label={ariaLabel}
        >
            {tokens.map((token) => (
                <StreamingToken
                    key={token.id}
                    content={token.content}
                    isVisible={token.isVisible}
                    isNewline={token.isNewline}
                    animationDurationMs={
                        // Tokens released together by `maxTypingDurationMs` fade
                        // as one block, so they get their own duration
                        bulkRevealStartIndex !== null &&
                        token.index >= bulkRevealStartIndex
                            ? mergedTiming.bulkRevealDurationMs
                            : mergedTiming.tokenAnimationDurationMs
                    }
                    sx={tokenSx}
                />
            ))}
        </Typography>
    );
}
